import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, X, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function UploadPage() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15MB)"); return; }
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`Archiviato in ${r.data.folder_name || r.data.category}`);
      nav(`/app/documenti/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il caricamento");
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="mb-10">
        <div className="overline text-[#0A0A0A] mb-3">— CARICA</div>
        <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Nuovo documento</h1>
        <p className="text-sm text-[#5C5C5C] max-w-2xl">
          Foto, PDF o scansione. L'AI legge, estrae i dati e riconosce automaticamente
          se è utile per <b>ISEE</b> (CU, giacenza media, patrimonio, affitti, disabilità)
          o per <b>730/CU</b> (spese sanitarie, mutuo, ristrutturazioni, scuola, veterinarie, ecc.).
          Se il sistema non riconosce, puoi aggiungerlo tu manualmente dal dettaglio del documento.
        </p>
      </div>

      {!file ? (
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-16 text-center cursor-pointer transition-colors ${
            dragOver ? "border-[#0A0A0A] bg-[#0A0A0A]/5" : "border-black/15 bg-white hover:bg-[#F8F8F6]"
          }`}
        >
          <div className="w-14 h-14 mx-auto bg-[#0A0A0A]/10 flex items-center justify-center rounded-xl mb-6">
            <UploadCloud className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display font-bold text-2xl mb-2 tracking-tight">Trascina qui il tuo file</h3>
          <p className="text-sm text-[#5C5C5C] mb-6">o scegli come caricare — JPG, PNG, WEBP, PDF (max 15MB)</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button
              data-testid="upload-select-btn"
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white"
            >
              <UploadCloud className="w-4 h-4 mr-2" /> Seleziona file
            </Button>
            <Button
              data-testid="upload-camera-btn"
              onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
              variant="outline"
              className="rounded-xl border-black/20 hover:bg-black/5"
            >
              <Camera className="w-4 h-4 mr-2" /> Scatta foto
            </Button>
          </div>
          <input
            ref={inputRef}
            data-testid="upload-file-input"
            type="file"
            className="hidden"
            accept="image/*,application/pdf"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <input
            ref={cameraRef}
            data-testid="upload-camera-input"
            type="file"
            className="hidden"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-black/10 rounded-xl p-6">
            <div className="overline text-[#8A8A8A] mb-4">ANTEPRIMA</div>
            <div className="relative border border-black/10 rounded-xl overflow-hidden bg-[#FAFAFA] h-80 flex items-center justify-center">
              {uploading && <div className="scan-beam" />}
              {preview ? (
                <img src={preview} alt="preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center">
                  <FileText className="w-16 h-16 text-black/20 mx-auto mb-3" />
                  <div className="text-xs text-[#8A8A8A] font-mono-plex">{file.name}</div>
                </div>
              )}
            </div>
            <div className="mt-4 font-mono-plex text-xs space-y-2">
              <div className="flex justify-between border-b border-black/5 pb-2">
                <span className="text-[#8A8A8A]">NOME</span>
                <span className="truncate ml-4 max-w-[60%]" title={file.name}>{file.name}</span>
              </div>
              <div className="flex justify-between border-b border-black/5 pb-2">
                <span className="text-[#8A8A8A]">TIPO</span><span>{file.type || "n/a"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8A8A8A]">DIMENSIONE</span><span>{(file.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-xl p-6 flex flex-col">
            <div className="overline text-[#8A8A8A] mb-4">ELABORAZIONE</div>
            {uploading ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="relative w-24 h-24 mb-6">
                  <div className="absolute inset-0 border-4 border-[#0A0A0A]/20 rounded-full" />
                  <div className="absolute inset-0 border-4 border-t-[#0A0A0A] rounded-full animate-spin" />
                </div>
                <div className="font-display font-bold text-xl tracking-tight mb-2">AI al lavoro</div>
                <p className="text-sm text-[#5C5C5C] max-w-xs">
                  Sto leggendo il documento, estraggo esercente, data, importo e categoria…
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4 flex-1">
                  <Step label="OCR sul documento (immagini + PDF)" />
                  <Step label="Estrazione esercente, data, importo" />
                  <Step label="Rilevamento rilevanza ISEE / 730 / CU" />
                  <Step label="Categorizzazione + rinomina intelligente" />
                  <Step label="Archiviazione nella cartella" />
                </div>
                <div className="flex gap-2 mt-6">
                  <Button
                    data-testid="upload-cancel-btn"
                    variant="outline"
                    className="rounded-xl border-black/20 hover:bg-black/5"
                    onClick={() => { setFile(null); setPreview(null); }}
                  >
                    <X className="w-4 h-4 mr-2" /> Cambia
                  </Button>
                  <Button
                    data-testid="upload-confirm-btn"
                    onClick={submit}
                    className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white flex-1"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Elabora e archivia
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ label }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#5C5C5C]">
      <div className="w-1.5 h-1.5 bg-[#0A0A0A] rounded-full" />
      {label}
    </div>
  );
}
